import React, { useState, FunctionComponent, useEffect } from "react";
import styles from "./Form.module.scss";
import FormProps from "./FormProps.interface";

import Alerts from "./components/Alerts/Alerts";
import FormFields from "./components/FormFields/FormFields";

import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import BubbleBackground from "components/BubbleBackground/BubbleBackground";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getUmbracoEndPoint from "app/umbraco/helpers/UmbracoEndPoint";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import urlBuilder from "app/umbraco/helpers/urlBuilder";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";
import isBrowserContext from "helpers/isBrowserContext";

const Form: FunctionComponent<FormProps> = (props) => {
    const wrapperStyles = getWrapperClasses(props.settings);
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    const formFields = props.content.form?.pages[0]?.fieldSets[0]?.containers[0]?.fields;
    const formFieldsExist = formFields && formFields.length > 0;

    const [isAlert, setIsAlert] = useState(false);
    const [currentAlerts, setCurrentAlerts] = useState<string[]>([]);
    const [formFieldsState, setFormFieldsState] = useState({});
    const [loading, setLoading] = useState(false);
    const [successAlert, setSuccessAlert] = useState<string | undefined>(undefined);
    const [success, setSuccess] = useState(false);
    const [resetFields, setResetFields] = useState(false);

    const [requiredFieldsForm, setRequiredFieldsForm] = useState<string[]>([]);
    const [requiredFieldsEach, setRequiredFieldsEach] = useState<string[]>([]);
    const requiredMessage = props.content.form?.requiredErrorMessage;
    const successMessage = props.content.form?.messageOnSubmit;

    useEffect(() => {
        const requiredFields: string[] = [];

        if (formFields && formFields.length) {
            formFields.forEach((field: { mandatory: boolean; alias: string }) => {
                if(field.mandatory) requiredFields.push(field.alias);
            });
        }

        setRequiredFieldsForm(requiredFields);
    }, []);

    const formWidthClass = props.settings.formWidth ? styles[props.settings.formWidth] : undefined;

    const entriesArr: Record<string, unknown>[] = [];
    Object.entries(formFieldsState).forEach(entry => {
        entriesArr.push({ alias: entry[0], value: entry[1] });
    });

    useEffect(() => {
        if (isAlert === true && currentAlerts.length === 0) {
            setIsAlert(false);
        }
    }, [currentAlerts]);

    useEffect(() => {
        const sameValues = requiredFieldsForm.sort().join(",") === requiredFieldsEach.sort().join(",");
        if (sameValues && currentAlerts.includes(requiredMessage)) {
            setCurrentAlerts(currentAlerts.filter((e => e !== requiredMessage)));
        }
    }, [requiredFieldsEach]);

    const setSuccessAndHide = () => {
        setSuccessAlert(successMessage);
        setLoading(false);
        setResetFields(false);
        setFormFieldsState({});
        setRequiredFieldsEach([]);
        if (success === false) setSuccess(true);
        setTimeout(() => {
            setSuccess(false);
        }, 5000);
    };

    const checkRequiredArrays = () => {
        const sameValues = requiredFieldsForm.sort().join(",") === requiredFieldsEach.sort().join(",");
        if (!sameValues && !currentAlerts.includes(requiredMessage)) {
            setCurrentAlerts([...currentAlerts, requiredMessage]);
        }
        return sameValues;
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const url = urlBuilder("/api/form/submit");
        const body = { formId: props.content.form.id, fields: [...entriesArr] };

        //Compare Arrays to check if all required fields are completed
        const arraysCheck = checkRequiredArrays();

        if (currentAlerts.length > 0 || entriesArr.length === 0 || !arraysCheck) {
            setIsAlert(true);
        }
        else if(!isAlert && arraysCheck) {
            setIsAlert(false);
            setLoading(true);
            setResetFields(true);
            try {
                fetch(url.toString(), {
                    method: "POST",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json",
                        "Origin": getUmbracoEndPoint(),
                        "X-Request-Domain": getUmbracoEndPoint()
                    },
                    body: JSON.stringify(body)
                }).then(() => { setSuccessAndHide(); });
            }
            catch (error) {
                throw new Error("Sending Failed.");
            }
        }
    };

    // ANIMATION
    const controls = useAnimation();
    const { ref, inView } = useInView({ threshold: 0.3 });

    const animateUp = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: 80, opacity: 0 }
    };
    const animateDown = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: -80, opacity: 0 }
    };

    const selectAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content._contentType : undefined;
    const selectVariants = selectAnimation === "animateUp" ? animateUp : selectAnimation === "animateDown" ? animateDown : undefined;
    const repeatAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content.repeatAnimation : false;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
        if (!inView && repeatAnimation) {
            controls.start("hidden");
        }
    }, [controls, inView]);

    return (
        <motion.section className={combineClasses([styles.wrapper, ...wrapperStyles])} id={identifier} ref={ref} initial={isBrowserContext() ? "hidden" : "shown"} animate={controls} variants={selectVariants} transition={{ duration: 0.4 }}>
            <div className={combineClasses([styles.container, formWidthClass])}>
                {/* Headline */}
                {props.content.headline ? (
                    <DynamicHeading className={styles.headline}>{props.content.headline}</DynamicHeading>
                ) : null}

                {/* Tagline */}
                {props.content.tagline ? (
                    <span className={styles.tagline}>{props.content.tagline}</span>
                ) : null}

                <form action="POST" className={styles.form} onSubmit={handleSubmit}>

                    {formFieldsExist ? formFields.map((formField: any, index: number) => {
                        return <FormFields key={index} resetFields={resetFields} isAlert={isAlert} setIsAlert={setIsAlert} setRequiredFieldsEach={setRequiredFieldsEach} requiredFieldsEach={requiredFieldsEach} setCurrentAlerts={setCurrentAlerts} currentAlerts={currentAlerts} formFieldsState={formFieldsState} setFormFieldsState={setFormFieldsState} {...formField} />;
                    }) : null}

                    <div className={styles.button_and_loading}>
                        <button className={styles.button} type="submit">
                            <span>{props.content.form?.submitLabel ? props.content.form.submitLabel : "Submit"}</span>
                        </button>
                        {loading ? (
                            <div className={styles.loader}>Sending...</div>
                        ) : null}
                    </div>
                </form>
                <Alerts alerts={currentAlerts} success={success} successAlert={successAlert} />
            </div>

            {props.settings.bubbleBackground && props.settings.bubbleBackground.length > 0 ? (
                <BubbleBackground {...props.settings.bubbleBackground[0]} />
            ) : null}
        </motion.section>
    );
};

export default Form;
